import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import * as microsoftTeams from "@microsoft/teams-js";
import { translations } from "../../../translations";
import { useTranslate } from "../../../hook/useTranslate";
import { setModalWithData, useModalSelector } from "../../../redux/reducers/ModalReducer/ModalReducer";
import { useAppConfigurationSelector } from "../../../redux/reducers/AppConfigurationReducer/AppConfigurationReducer";
import { IAppFrom } from "../../../interfaces/IAppFrom";
import { submitFeedback } from "../../../apis/Reviews/ReviewsApi";
/** Deeplink Teams pour ouvrir la modal d'installation / page de l'app dans Teams */
const TEAMS_APP_INSTALL_DEEPLINK_BASE = "https://teams.cloud.microsoft/l/app";
/** URL par défaut du formulaire de feedback complet (lien "Fill out our complete feedback form") */
const DEFAULT_FEEDBACK_FORM_URL = "https://forms.office.com/e/ayyKcmJpSt?embed=true";
/** Par app (from) : formulaire de feedback, ID Teams et URL API pour soumettre le feedback */
const APP_REVIEW_CONFIG = {
    [IAppFrom.BOOKING]: {
        feedbackFormUrl: "https://forms.office.com/e/ayyKcmJpSt?embed=true",
        teamsAppId: "8bbbc813-6d87-497c-9e7c-e401607b2347",
        feedbackSubmitApiUrl: "/api/v1/feedback",
    },
    [IAppFrom.CALENDAR]: {
        feedbackFormUrl: "https://forms.office.com/e/ayyKcmJpSt?embed=true",
        teamsAppId: "19a29a41-cbca-4152-a2af-dd9728ea35f1",
        feedbackSubmitApiUrl: "/api/v1/feedback",
    },
    [IAppFrom.CONTACT]: {
        feedbackFormUrl: "https://forms.office.com/e/ayyKcmJpSt?embed=true",
        teamsAppId: "b4f85a61-fd95-49b8-8fb3-3cb473412407",
        feedbackSubmitApiUrl: "/api/v1/feedback",
    },
    [IAppFrom.GANTT]: {
        feedbackFormUrl: "https://forms.office.com/e/ayyKcmJpSt?embed=true",
        teamsAppId: "dc00c22b-b7ad-4db8-bf35-c2660104d672",
        feedbackSubmitApiUrl: "/api/v1/feedback",
    },
    [IAppFrom.KEEPASS]: {
        feedbackFormUrl: "https://forms.office.com/e/ayyKcmJpSt?embed=true",
        teamsAppId: "dc00c22b-b7ad-4db8-bf35-c2660104d622",
        feedbackSubmitApiUrl: "/api/v1/feedback",
    },
    [IAppFrom.MAP]: {
        feedbackFormUrl: "https://forms.office.com/e/ayyKcmJpSt?embed=true",
        teamsAppId: "ae0e3172-ce0a-4fe7-b712-6ebda56781e3",
        feedbackSubmitApiUrl: "/api/v1/feedback",
    },
    [IAppFrom.PARKING]: {
        feedbackFormUrl: "https://forms.office.com/e/ayyKcmJpSt?embed=true",
        teamsAppId: "5148fb27-1ab7-487b-af5c-f2adf959dca8",
        feedbackSubmitApiUrl: "/api/v1/feedback",
    },
    [IAppFrom.REMINDS]: {
        feedbackFormUrl: "https://forms.office.com/e/ayyKcmJpSt?embed=true",
        teamsAppId: "a1b6cbb4-900d-49a5-8aae-a5985f5a6951",
        feedbackSubmitApiUrl: "/api/v1/feedback",
    },
    // Autres apps à ajouter : Map, Booking, Parking, Contact, Keepass, Gantt, Reminds
};
/** Last resort only: every host sets `app` at boot, so this should never surface. */
const DEFAULT_PRODUCT_NAME = "Calendar Pro";
export const useReviewDialog = (config) => {
    const dispatchCtx = useDispatch();
    const t = useTranslate(translations);
    const { app, appName } = useAppConfigurationSelector("app", "appName");
    const { isOpen } = useModalSelector("isOpen");
    const [step, setStep] = useState("rating");
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [comment, setComment] = useState("");
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    // `app` is the product name every host sets at boot ("Gantt Chart Pro", "KeePass Pro", ...).
    // `appName` is only a fallback: keepass and the calendar family put the user's tab name there.
    // `||` rather than `??`: the reducer initialises both to "" before the app boots.
    const productName = config?.productName || app || appName || DEFAULT_PRODUCT_NAME;
    // Read through `translations.get` rather than `t`: useTranslate memoizes on the key alone,
    // so an interpolated title would stick to whatever appName was on the very first render.
    const reviewTitle = translations.get("ReviewTitleJoin", { appName: productName });
    const onClose = useCallback(() => {
        dispatchCtx(setModalWithData({ isOpen: undefined, data: undefined }));
    }, [dispatchCtx]);
    const onStarHover = useCallback((value) => setHoverRating(value), []);
    const onSubmitFeedback = useCallback(async () => {
        setIsSubmittingFeedback(true);
        try {
            await submitFeedback(comment);
            onClose();
        }
        catch {
            setIsSubmittingFeedback(false);
        }
    }, [comment, onClose]);
    const onMaybeLater = useCallback(() => {
        onClose();
    }, [onClose]);
    /** URL AppSource (fallback si pas de teamsAppId) */
    const appSourceReviewUrl = config?.appSourceReviewUrl ??
        (config?.appSourceProductId
            ? `https://appsource.microsoft.com/en-us/product/office/${config.appSourceProductId}?tab=Reviews`
            : null);
    /** Form URL et teamsAppId selon "from" (switch pour chaque app) */
    let feedbackFormUrl;
    let effectiveTeamsAppId;
    switch (config?.from) {
        case IAppFrom.CALENDAR:
            feedbackFormUrl = APP_REVIEW_CONFIG[IAppFrom.CALENDAR].feedbackFormUrl;
            effectiveTeamsAppId = APP_REVIEW_CONFIG[IAppFrom.CALENDAR].teamsAppId;
            break;
        // case IAppFrom.MAP: ...
        // case IAppFrom.BOOKING: ...
        // case IAppFrom.PARKING: ...
        // case IAppFrom.CONTACT: ...
        // case IAppFrom.KEEPASS: ...
        // case IAppFrom.GANTT: ...
        // case IAppFrom.REMINDS: ...
        default:
            feedbackFormUrl = config?.feedbackFormUrl ?? DEFAULT_FEEDBACK_FORM_URL;
            effectiveTeamsAppId = config?.teamsAppId;
            break;
    }
    const openTeamsInstallModal = useCallback((teamsAppId) => {
        const deeplink = `${TEAMS_APP_INSTALL_DEEPLINK_BASE}/${teamsAppId}?source=app-details-dialog`;
        microsoftTeams.app
            .openLink(deeplink)
            .then(() => onClose())
            .catch(() => {
            window.open(deeplink, "_blank", "noopener,noreferrer");
            onClose();
        });
    }, [onClose]);
    const onRate = useCallback((value) => {
        setRating(value);
        if (value >= 4 && effectiveTeamsAppId) {
            openTeamsInstallModal(effectiveTeamsAppId);
        }
        else if (value >= 4 && appSourceReviewUrl) {
            window.open(appSourceReviewUrl, "_blank", "noopener,noreferrer");
            onClose();
        }
        else if (value >= 4) {
            setStep("appsource");
        }
        else if (value >= 1) {
            setStep("feedback");
        }
    }, [effectiveTeamsAppId, appSourceReviewUrl, onClose, openTeamsInstallModal]);
    const resetToRating = useCallback(() => {
        setStep("rating");
        setRating(0);
        setHoverRating(0);
        setComment("");
    }, []);
    return {
        t,
        onClose,
        isOpen,
        step,
        rating,
        hoverRating,
        comment,
        setComment,
        onRate,
        onStarHover,
        onSubmitFeedback,
        onMaybeLater,
        productName,
        reviewTitle,
        appSourceReviewUrl,
        feedbackFormUrl,
        resetToRating,
        isSubmittingFeedback,
    };
};
